import { Grid, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { ActionBar, Header } from 'src/components/header';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { queryClient } from 'src/components/queryProvider';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useModal, useParamQuery } from 'src/hooks';
import { useSaveBulkReplaceRequest } from 'src/hooks/api/domainSpecific/bulkReplace/useSaveBulkReplaceRequest';
import { useDeleteBulkReplaceRequest } from 'src/hooks/api/publication/useDeleteStageAssembly';
import { PublicationTypes } from 'src/interfaces';
import { DataTestID, lang, queryKeys, useUserRole } from 'src/utils';

import {
  ModalActionType,
  PUBLICATION_STATUS,
} from './AssemblyPublicationPage.interface';
import {
  ContentContainer,
  PageContainer,
} from './AssemblyPublicationPage.styled';
import { useAssemblyPublicationListPage } from './hooks/AssemblyPublicationPage.hook';
import { AssemblyPublicationFooter } from './modules/AssemblyPublicationFooter/AssemblyPublicationFooter';
import { AssemblyPublicationTable } from './modules/AssemblyPublicationTable/AssemblyPublicationTable';

export const AssemblyPublicationPage = () => {
  const { onChangeParams, activeParams } = useParamQuery({
    status: [PUBLICATION_STATUS.STAGED],
  });
  const [activeTab, setActiveTab] = useState(
    activeParams?.status?.[0] === PUBLICATION_STATUS.SAVED ? 1 : 0,
  );
  const [activeRow, setActiveRow] = useState('');

  const [selectedId] = activeRow.split(' - ');

  const { mutateAsync: removeStageAssembly, isLoading: isDeleting } =
    useDeleteBulkReplaceRequest();

  const { mutateAsync: saveStageAssembly, isLoading: isSaving } =
    useSaveBulkReplaceRequest();

  const stageAssemblyModal = useModal();
  const publicationModal = useModal();
  const [modalAction, setModalAction] = useState<string | ModalActionType>('');

  const openDeleteModal = () => {
    setModalAction(lang.deleteText as ModalActionType);
    stageAssemblyModal.onOpenModal();
  };

  const openPublishModal = () => {
    publicationModal.onOpenModal();
  };
  const onClosePublishModal = () => {
    publicationModal.onCloseModal();
  };
  const openSaveModal = () => {
    setModalAction(lang.save as ModalActionType);
    stageAssemblyModal.onOpenModal();
  };

  const handlePublishSuccess = () => {
    // Clear active row selection
    setActiveRow('');

    // Force refetch by removing queries from cache
    queryClient.invalidateQueries({
      queryKey: queryKeys.bulkReplaceRequests.invalidateAll(),
    });
  };

  const handleConfirm = () => {
    if (modalAction === lang.deleteText) {
      handleRemoveStageAssembly();
      stageAssemblyModal.onCloseModal();
    } else {
      handleSaveAssembly();
      stageAssemblyModal.onCloseModal();
    }
  };

  const handleSaveAssembly = async () => {
    await saveStageAssembly(Number(selectedId));
    setActiveRow('');
    Snackbar.success(`${lang.successfullySaveStageAssembly}`);
  };

  const handleRemoveStageAssembly = async () => {
    const selectedData = assemblyPublicationTableData.find(
      ({ id }) => id === Number(selectedId),
    );
    await removeStageAssembly({
      id: Number(selectedId),
      replaceName: selectedData?.display_name,
    });
    setActiveRow('');
    Snackbar.success(`${lang.successfullyDeleteStageAssembly}`);
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setActiveRow('');
    onChangeParams('status', [
      newValue === 0 ? PUBLICATION_STATUS.STAGED : PUBLICATION_STATUS.SAVED,
    ]);
  };

  const {
    isLoading,
    getNextListPublicationPage,
    columns,
    listPublication,
    hasNextPage,
    handleSort,
    activeColumn,
    sorting,
  } = useAssemblyPublicationListPage();
  const { isViewOnly } = useUserRole();

  const assemblyPublicationTableData = listPublication?.pages
    ? listPublication.pages.flatMap((page) => page.data)
    : [];

  const hasData =
    listPublication &&
    listPublication?.pages.flatMap((page) => page.data).length > 0;

  const assemblyIds = assemblyPublicationTableData.flatMap(
    (a) => a.assemblyIds,
  );

  const hasInvalidAssemblyPublicationRows = assemblyPublicationTableData.some(
    ({ hasLocations }) => !hasLocations,
  );
  const hasValidAssemblyPublicationRows = assemblyPublicationTableData.some(
    ({ hasLocations }) => hasLocations,
  );
  const disablePublishForMissingNsns =
    !!activeTab && hasData && !hasValidAssemblyPublicationRows;
  const publishDisabledMessage = disablePublishForMissingNsns
    ? lang.bulkAssmeblyPublicationModal.invalidOnlyMessage
    : undefined;
  const publishModalAlertMessage =
    !!activeTab &&
    hasInvalidAssemblyPublicationRows &&
    hasValidAssemblyPublicationRows
      ? lang.publicationModal.assemblyWithoutNsnsWarning
      : undefined;

  return (
    <PageContainer>
      <ContentContainer>
        <Grid>
          <Header
            breadcrumbItems={[
              {
                label: lang.locations,
                link: `${PATHS.LOCATIONS}`,
              },
              {
                label: lang.publications,
                link: `${PATHS.LIST_PUBLICATIONS}`,
              },
              {
                label: lang.assemblyPublications,
              },
            ]}
            title={lang.assemblyPublications}
            actionBar={<ActionBar type={lang.assemblyPublications} />}
          />
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            style={{ borderBottom: '1px solid' }}>
            <Tab label={lang.stagedChanges} sx={{ textTransform: 'none' }} />
            <Tab
              label={lang.assemblyPublications}
              data-testid={DataTestID.AssemblyPublicationSavedTab}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          <AssemblyPublicationTable
            setActiveRow={setActiveRow}
            activeRow={activeRow}
            isLoading={isLoading}
            getNextListPublicationPage={getNextListPublicationPage}
            columns={columns}
            listPublication={assemblyPublicationTableData}
            hasNextPage={hasNextPage}
            handleSort={handleSort}
            activeColumn={activeColumn}
            sorting={sorting}
            hasData={hasData}
            activeTab={activeTab}
          />
        </Grid>
        <AssemblyPublicationFooter
          saveLabel={!activeTab ? lang.save : lang.publish}
          disableSave={
            !hasData ||
            (!activeTab && !activeRow) ||
            disablePublishForMissingNsns
          }
          saveDisabledMessage={publishDisabledMessage}
          hideDelete={!!activeTab || isViewOnly}
          hideSave={isViewOnly}
          disableExport={!hasData}
          disableDelete={!activeRow || !hasData}
          onDelete={openDeleteModal}
          onSave={!activeTab ? openSaveModal : openPublishModal}
          isDeleting={isDeleting}
          isSaving={isSaving}
        />

        <SimpleDialog
          open={stageAssemblyModal.open}
          cancelText={lang.cancel}
          confirmText={
            modalAction === lang.deleteText ? lang.deleteText : lang.confirm
          }
          title={
            modalAction === lang.deleteText
              ? lang.removeStageAssemblyModal.title
              : lang.saveNow
          }
          subTitle={
            modalAction === lang.deleteText
              ? lang.removeStageAssemblyModal.subTitle
              : lang.saveToCreativeCenter
          }
          onClose={stageAssemblyModal.onCloseModal}
          onClickCancel={stageAssemblyModal.onCloseModal}
          modalSize="sm"
          onClickConfirm={handleConfirm}
          actionColor={modalAction === lang.deleteText ? 'error' : 'primary'}
        />

        <PublicationModal
          publicationType={lang.publicationType as PublicationTypes}
          assemblyIds={assemblyIds}
          open={publicationModal.open}
          onClose={onClosePublishModal}
          onPublished={handlePublishSuccess}
          title={lang.bulkAssmeblyPublicationModal.title}
          subTitle={lang.bulkAssmeblyPublicationModal.subTitle}
          alertMessage={publishModalAlertMessage}
        />
      </ContentContainer>
    </PageContainer>
  );
};
